import { LinkOutlined, InfoCircleFilled, CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { Popover } from "antd";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import Toast from "src/components/Common/Toast";
import { DISQUALIFY_REASONS, MIXPANEL_EVENTS } from "src/constants/globalConstants";
import { storage } from "src/services/config/storage";
import { convertToStyle, getOverallVerdict, getTraitBand } from "../oprations";
import WithToolTip from "src/components/Common/WithToolTip";
import { CheckIcon, Cross, DownloadWhite, TrustScore } from "src/assets/icons";
import { calculateTimeTaken, roundOff } from "src/utils/helperFunctions";
import { ReportPdfContext } from "src/utils/contexts";
import ReportDownloadComponent from "src/components/Common/ReportDownloadComponent";
import MiniReportDownloadComponent from "src/components/Common/ReportDownloadComponent/MiniReportDownloadComponent";
import { TrustScorePill } from "src/components/Common/TrustScore";
import ConfirmationModal from "src/components/Common/ConfirmationModal";
import { HIDE_DOWNLOAD_REPORT_ORGS, HIDE_EMAIL_FOR_LINK_IDS, languageOptions } from "../constant";
import HeaderProfile from "../V2/Report40/HeaderProfile";

export const ReportHeader = ({
  reportData,
  linkConfig,
  positionConfig,
  userVideoSessionsList,
  isNonDnaReport,
  logMixpanelEvent,
  hideCta = false,
  fetchSnapshotsForReportDownload,
  referenceImageLink,
  showProctoringModal,
  hideFitScore = false,
  companyId,
  hideVerdictPills = false,
  candidateFormData,
  // "profile": the web report's 4.0 header (V2). The PDF and headerless views keep this one.
  layout = "default",
  onSkillScores,
}) => {

  const [isDownloadReportVisible, setIsDownloadReportVisible] = useState(false)
  const [showProctoringOptionModal, setShowProctoringOptionModal] = useState(false)
  const [includeProctoring, setIncludeProctoring] = useState(null)
  const hasAutoDownloadTriggered = useRef(false)
  const history = useHistory();
  const { userId, linkId } = useParams();
  const { search } = useLocation()

  /**
   * 
   * useMemo
   * 
   */
  const params = useMemo(() => new URLSearchParams(search), [search])
  const isCandidateReport = useMemo(() => history?.location?.pathname?.includes("candidate-report"), [history])
  const hideEmailForLinkId = HIDE_EMAIL_FOR_LINK_IDS[linkId];

  // Re-scaling report header and content changes
  const isMinimalHeaderReport = useMemo(() => params?.get("headerless")?.toLowerCase() === "true", [params])
  const isHideHeaderForReport = useMemo(() => params?.get("hideHeader")?.toLowerCase() === "true", [params])

  // Mini report is driven purely by the company's organizationConfig.miniReport flag.
  // positionConfig comes from /api/position/v1/position-config, which nests the org config.
  const isMiniReportConfigured = useMemo(
    () => positionConfig?.organization?.organizationConfig?.miniReport === true,
    [positionConfig]
  )

  // Undocumented escape hatch: managers at a mini-report company can append
  // ?rv=full to fall back to the complete report. Only meaningful when the mini
  // report is configured -- everyone else already gets the full report.
  const isFullReportOverride = useMemo(
    () => params?.get("rv")?.toLowerCase() === "full",
    [params]
  )

  const isMiniReportEnabled = isMiniReportConfigured && !isFullReportOverride

  // Shown on every cutoff-enabled position -- the same condition that drives the
  // CUTOFF CLEARED pill below and the Overall Summary block.
  const passFailPill = useMemo(
    () => reportData?.cutOffCleared == null ? null : getOverallVerdict(reportData, positionConfig),
    [reportData, positionConfig]
  );

  const isProctoringAvailable = useMemo(() => {
    return !!(linkConfig?.proctoring ||
             linkConfig?.saveProctoredVideo ||
             userVideoSessionsList?.length > 0 ||
             positionConfig?.screenProctoring ||
             positionConfig?.audioProctoring);
  }, [linkConfig, userVideoSessionsList, positionConfig]);

  const shouldUseMiniPdfDownload = isMiniReportEnabled;

  const { reportPdfView } = useContext(ReportPdfContext)

  // Trust score pill beside the candidate name, web view only: the mini pdf
  // shows the same score in its proctoring details section instead.
  const trustScore = reportData?.trustScore;
  const hideTrustScoreDescription = positionConfig?.skipAiProcOnCutoffFail && reportData?.cutOffCleared === false;
  const [trustScoreColor, trustScoreBg, trustScoreVal] = useMemo(() => {
    let textColor = "#B6B6B6";
    let bgColor = "#fff";
    const trustScoreNum = Number(trustScore?.trustScore?.toString()?.split("%")?.[0]);
    if (trustScore?.remark) {
      const bandStyle = getTraitBand({
        median: 50,
        lb: 25,
        ub: 75,
        score: roundOff(trustScoreNum),
      });
      textColor = bandStyle.textColor;
      bgColor = bandStyle.color;
    }
    return [textColor, bgColor, trustScoreNum];
  }, [trustScore]);

  useEffect(() => {
    if (reportData?.position?.organizationId && !HIDE_DOWNLOAD_REPORT_ORGS.includes(reportData?.position?.organizationId)
      && !isMinimalHeaderReport
      && !reportPdfView
    ) {
      setIsDownloadReportVisible(true)
    } else {
      setIsDownloadReportVisible(false)
    }
  }, [reportPdfView, isMinimalHeaderReport, reportData])

  const handleBackClick = () => {
    // Send back to previous route regardless
    // Edge case in previous scenario breaks filter state on listing page when refreshed on report
    history.goBack();
  };

  const onUrlCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    Toast("success", "URL Successfully Copied", "Copied!", "bottomLeft");
    logMixpanelEvent(MIXPANEL_EVENTS.CopiedLink);
  };

  const openTutorial = () => {
    const tutorialLink = process.env.REACT_APP_TUTORIAL_LINK;
    if (tutorialLink) {
      logMixpanelEvent(MIXPANEL_EVENTS.ViewedHowToReadReport);
      window.open(process.env.REACT_APP_TUTORIAL_LINK, "_blank");
    } else {
      Toast("error", "Something went wrong");
    }
  };

  const onViewFullReport = () => {
    logMixpanelEvent(MIXPANEL_EVENTS.ViewedFullReport);
    window.open(`/pdf-report/link/${linkId}/users/${userId}`, "_blank");
  };

  const reportDuration = calculateTimeTaken(reportData?.startedAt, reportData?.completedAt);

  const isAutoDownload = useMemo(() => params?.get("autoDownload")?.toLowerCase() === "true", [params]);

  const handleDownloadClick = () => {
    // Mini pdf never includes proctoring snapshots, so skip the choice modal
    if (isProctoringAvailable && !isMiniReportEnabled) {
      setShowProctoringOptionModal(true);
    } else {
      setIncludeProctoring(false);
    }
  };

  // Auto-download: skip modal, trigger download once.
  // Mini pdf never includes proctoring snapshots.
  useEffect(() => {
    if (hasAutoDownloadTriggered.current) return;
    if (isAutoDownload && reportData && positionConfig !== null && linkConfig !== null) {
      hasAutoDownloadTriggered.current = true;
      setIncludeProctoring(isProctoringAvailable && !isMiniReportEnabled ? true : false);
    }
  }, [isAutoDownload, reportData, positionConfig, linkConfig, isMiniReportEnabled]);

  const handleProctoringChoice = (include) => {
    setShowProctoringOptionModal(false);
    setIncludeProctoring(include);
  };

  const resetDownloadState = () => {
    setIncludeProctoring(null);
  };

  const candidateName = reportData?.user?.name;
  const candidateEmail = reportData?.user?.email;
  const candidatePhone = reportData?.user?.phone;
  const positionName = reportData?.position?.name;
  const expertiseLevel = reportData?.user?.expertiseLevel?.split("-").join(" ");
  const candidateMeta = [positionName, expertiseLevel].filter(Boolean).join(" • ");
  const reportLanguage = languageOptions[reportData?.language];
  const completedDate = reportData?.completedDate ? new Date(reportData.completedDate) : null;
  const hasValidCompletedDate = completedDate && !Number.isNaN(completedDate.getTime());

  const showBack = !isMinimalHeaderReport && !reportPdfView && (history.action !== "POP" || storage.checkAuthentication()) && params?.get("parent")?.toLowerCase() !== "cc" && !isCandidateReport;
  const showProctoringButton = !reportPdfView && !isHideHeaderForReport && isProctoringAvailable;
  const cameraNotWorking = (linkConfig?.proctoring || (linkConfig?.saveProctoredVideo || userVideoSessionsList?.length)) && reportData?.cameraSupported === false;
  const cameraWarning = cameraNotWorking
    ? <Popover
      color={"#9e9e9e"}
      overlayInnerStyle={{ borderRadius: '20px' }}
      content={(
        <span className="text-white">
          Camera not working
        </span>
      )}
    >
      <InfoCircleFilled style={{ color: 'red' }} className="ml-6" />
    </Popover> : <></>;

  const downloadButton = isDownloadReportVisible && !isHideHeaderForReport
    && (shouldUseMiniPdfDownload ? (
      <MiniReportDownloadComponent
        reportData={reportData}
        positionConfig={positionConfig}
        hideFitScore={hideFitScore}
        isNonDnaReport={isNonDnaReport}
        includeProctoring={includeProctoring}
        isProctoringAvailable={isProctoringAvailable}
        onDownloadClick={handleDownloadClick}
        onDownloadComplete={resetDownloadState}
        candidateFormData={candidateFormData}
        referenceImageLink={referenceImageLink}
      />
    ) : (
      <ReportDownloadComponent
        reportData={reportData}
        positionConfig={positionConfig}
        fetchSnapshots={fetchSnapshotsForReportDownload}
        referenceImageLink={referenceImageLink}
        hideFitScore={hideFitScore}
        includeProctoring={includeProctoring}
        onDownloadClick={handleDownloadClick}
        isProctoringAvailable={isProctoringAvailable}
        onDownloadComplete={resetDownloadState}
        candidateFormData={candidateFormData}
      />
    ));

  const proctoringChoiceModal = (
    <ConfirmationModal
      showModal={showProctoringOptionModal}
      onClose={() => setShowProctoringOptionModal(false)}
      onSubmit={handleProctoringChoice}
      confirmationText="Yes"
      cancelText="No"
    >
      <h4 className="text-16 semibold pb-10">Include proctoring snapshots in report?</h4>
      {/* <p className="text-14">Proctoring data includes snapshots and trust score information.</p> */}
    </ConfirmationModal>
  );

  if (layout === "profile" && !isMinimalHeaderReport && !reportPdfView) {
    const showEmail = !hideEmailForLinkId && candidateEmail && !candidateEmail.includes("example.com");
    const workExperience = candidateFormData?.workExperience || candidateFormData?.yearsOfExp;
    const city = candidateFormData?.currentLocation || candidateFormData?.city;
    return (
      <>
        <HeaderProfile
          reportData={reportData}
          passFailPill={passFailPill}
          hideVerdictPills={hideVerdictPills}
          disqualifyReason={DISQUALIFY_REASONS[reportData?.disqualifyReason] ?? reportData?.disqualifyReason}
          showBack={showBack}
          onBack={handleBackClick}
          email={showEmail ? candidateEmail : null}
          phone={candidatePhone}
          chips={[positionName, expertiseLevel, workExperience, city].filter(Boolean).map(String)}
          reportDuration={reportDuration}
          reportLanguage={reportLanguage || languageOptions.en}
          completedDate={hasValidCompletedDate ? completedDate : null}
          hideFitScore={hideFitScore}
          trustScoreVal={trustScoreVal}
          isProctoringAvailable={isProctoringAvailable}
          candidateFormData={candidateFormData}
          onSkillScores={onSkillScores}
          actions={
            <>
              {showProctoringButton && (
                <button type="button" className="r4-btn r4-btn--dark" onClick={showProctoringModal}>
                  Proctoring review
                  {cameraWarning}
                </button>
              )}
              {!isCandidateReport && (
                <button type="button" className="r4-icon-btn" onClick={onUrlCopy} title="Copy link to clipboard" aria-label="Copy link to clipboard">
                  <LinkOutlined />
                </button>
              )}
              {downloadButton}
            </>
          }
        />
        {proctoringChoiceModal}
      </>
    );
  }

  return (
    <div
      className={`header ${isMinimalHeaderReport ? "minimal-header" : ""}`}
      style={{
        ...(reportPdfView && { marginBottom: "20px", padding: "10px 0" })
      }}
    >
      <div
        className="flex items-center justify-between max-width-left"
        style={{
          ...(reportPdfView && { padding: "20px 0" })
        }}
      >
        <div className="flex items-center">
          {
            showBack && (
              <>
                <div className="header--goBack" onClick={handleBackClick}>
                  <img className="icon" src="/img/right-arrow.png" alt="" />
                  <span className="text">Back</span>
                </div>
                <div className="divider" />
              </>
            )
          }
          <div className="candidate-bio">
            <div className="user-status flex items-start gap-2">
              {
                !isMinimalHeaderReport &&
                <div className="flex flex-col items-start">
                  {
                    candidateName
                      ? <h4 className="name">{candidateName}</h4>
                      : !reportPdfView
                        ? <h4 className="name">--</h4>
                        : <></>
                  }
                  {
                    !hideEmailForLinkId &&
                    candidateEmail &&
                    !candidateEmail.includes("example.com") &&
                    <h4 className="email mt-2">
                      {candidateEmail}
                    </h4>
                  }
                  {
                    candidatePhone &&
                    <h4 className="email mt-8">
                      {candidatePhone}
                    </h4>
                  }
                </div>
              }
              {
                !hideVerdictPills && reportData?.disqualified &&
                <div className="ml-10 uppercase">
                  <div
                    className="status-pill disqualified flex items-center space-between"
                    style={convertToStyle(
                      getTraitBand({
                        disqualified: reportData.disqualified
                      })
                    )}
                  >
                    <span className="text-14 text-red weight-600">Disqualified</span>
                    {
                      true &&
                      <WithToolTip
                        title={DISQUALIFY_REASONS[reportData.disqualifyReason] ?? reportData?.disqualifyReason}
                        showToolTip={true}
                      >
                        <InfoCircleFilled className="ml-4 text-red text-16" />
                      </WithToolTip>
                    }
                  </div>
                </div>
              }
              {
                !hideVerdictPills && reportData?.cutOffCleared != null
                  ? (<TrustScorePill
                    icon={reportData?.cutOffCleared === true ? <CheckIcon width={20} height={20} color={"#2C6740"} /> : <Cross width={20} height={20} fill={"#ff5267"} />}
                    text={reportData?.cutOffCleared === true ? "CUTOFF CLEARED" : "CUTOFF NOT CLEARED"}
                    bg={reportData?.cutOffCleared === true ? "#61C489" : ""}
                    color={reportData?.cutOffCleared === true ? "#2C6740" : "#ff5267"}
                    tooltipText={""}
                    className={"ml-10 width-190 height-30 mt-1"}
                  />) : <></>
              }
              {
                passFailPill &&
                <TrustScorePill
                  icon={passFailPill === "PASS"
                    ? <CheckCircleFilled style={{ color: "#2C6740", fontSize: 18 }} />
                    : passFailPill === "FAIL"
                      ? <CloseCircleFilled style={{ color: "#ff5267", fontSize: 18 }} />
                      : null
                  }
                  text={passFailPill}
                  bg={"transparent"}
                  color={passFailPill === "PASS" ? "#2C6740" : passFailPill === "PENDING" ? "#888888" : "#ff5267"}
                  tooltipText={""}
                  className={`ml-10 height-30 mt-1 width-80 ${passFailPill === "PENDING" ? "pl-16" : ""}`}
                />
              }
              {
                isMiniReportEnabled && !reportPdfView && trustScore?.remark &&
                <TrustScorePill
                  icon={<TrustScore color={trustScoreColor} />}
                  text={`${roundOff(trustScoreVal)}%`}
                  bg={trustScoreBg}
                  color={trustScoreColor}
                  tooltipText={hideTrustScoreDescription ? "" : trustScore.remark}
                  className={"ml-10 height-30 mt-1"}
                />
              }
            </div>
            {
              (candidateMeta || !reportPdfView) &&
              <p className="flex items-center meta capitalize">{candidateMeta || "--"}</p>
            }
            {reportDuration && <p className="meta capitalize">
              {`Total time taken: ${reportDuration} `}
            </p>}
            {
              (reportLanguage || !reportPdfView) && <p className="meta capitalize">
                Language Used: {reportLanguage || languageOptions.en}
              </p>
            }
            {
              hasValidCompletedDate && <p className="meta" style={{ color: "#000", fontSize: 14 }}>
                <span style={{ fontWeight: 700 }}>Completion date:</span>{" "}
                {completedDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) + ", " + completedDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}
              </p>
            }
          </div>
        </div>
        <div className="flex items-center justify-between">
          {
            !isMinimalHeaderReport && !reportPdfView && !isCandidateReport ? <LinkOutlined
              onClick={onUrlCopy}
              className="copy-url"
              title="Copy link to clipboard"
            /> : <></>
          }
          {downloadButton}
          {
            showProctoringButton
              ? <button
                className="default-btn mr-10 flex align-center items-center"
                onClick={showProctoringModal}
              >
                Proctoring
                {cameraWarning}
              </button>
              : <></>
          }
          {!hideCta && <button className="default-btn mr-10" onClick={openTutorial}>
            How to read this report ?
          </button>}
          {!hideCta && isNonDnaReport ? (
            <button className="view-long-report" onClick={onViewFullReport}>
              View Full Report <DownloadWhite />
            </button>
          ) : null}
        </div>
      </div>
      {proctoringChoiceModal}
    </div>
  );
};
